import React, { useState, useEffect } from "react";
import { View, TouchableOpacity } from "react-native";
import { StyleSheet } from "@/utils/appStyleSheet";
import { AppText } from "@/components/AppText";
import {
  RefundProcessItem,
  getPlatformName,
  getPhoneNumber,
} from "@/utils/refundProcessTypeGuards";
import { Ionicons, AntDesign } from "@expo/vector-icons";
import { formatPhoneNumber } from "@/utils/phoneFormatter";

const MAX_COUNTDOWN = 3;

type Props = {
  item: RefundProcessItem;
  onCountdownComplete: () => void;
  onClose?: () => void;
};

const InitiateCallContent: React.FC<Props> = ({
  item,
  onCountdownComplete,
  onClose,
}) => {
  const [countdown, setCountdown] = useState(MAX_COUNTDOWN);

  const platformName = getPlatformName(item);
  const phoneNumber = getPhoneNumber(item);

  useEffect(() => {
    setCountdown(MAX_COUNTDOWN);
    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);

          setTimeout(() => {
            onCountdownComplete();
          }, 1000);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [onCountdownComplete]);

  const countdownText =
    countdown > 0 ? `Call in ${countdown} seconds...` : "Calling now...";

  return (
    <>
      {/* Close Button */}
      <TouchableOpacity style={styles.closeButton} onPress={onClose}>
        <AntDesign name="close" size={20} color="#6B7280" />
      </TouchableOpacity>

      {/* Content */}
      <View style={styles.contentContainer}>
        <View style={styles.contentInner}>
          {/* Static Phone Icon */}
          <View style={styles.phoneIconContainer}>
            <Ionicons name="call" size={30} color="#FFFFFF" />
          </View>

          {/* Text content */}
          <View style={styles.textContainer}>
            <AppText style={styles.title}>
              We&apos;re calling {platformName} at
            </AppText>
            <AppText style={styles.phoneNumber}>
              {formatPhoneNumber(phoneNumber)}
            </AppText>

            <View style={styles.descriptionContainer}>
              <AppText style={styles.description}>
                You can listen in or close this window and we&apos;ll notify you
                with the result.
              </AppText>
            </View>
          </View>
        </View>

        {/* Button */}
        <View style={styles.buttonContainer}>
          <TouchableOpacity style={styles.countdownButton} disabled>
            <AppText style={styles.countdownButtonText}>
              {countdownText}
            </AppText>
          </TouchableOpacity>
        </View>
      </View>
    </>
  );
};

const styles = StyleSheet.create({
  contentContainer: {
    gap: 16,
    paddingTop: 20,
  },
  contentInner: {
    alignItems: "center",
    gap: 16,
  },
  phoneIconContainer: {
    width: 80,
    height: 80,
    borderRadius: 40,
    backgroundColor: "#0F9129",
    justifyContent: "center",
    alignItems: "center",
    marginBottom: 8,
  },
  textContainer: {
    gap: 4,
    alignItems: "center",
  },
  title: {
    fontSize: 24,
    fontWeight: "500",
    color: "#000000",
    textAlign: "center",
    lineHeight: 30,
  },
  phoneNumber: {
    fontSize: 24,
    fontWeight: "500",
    color: "#000000",
    textAlign: "center",
    lineHeight: 30,
  },
  descriptionContainer: {
    marginTop: 8,
  },
  description: {
    fontSize: 15,
    fontWeight: "400",
    color: "#000000",
    opacity: 0.5,
    textAlign: "center",
    lineHeight: 23,
    letterSpacing: -0.3,
  },
  buttonContainer: {
    gap: 8,
  },
  countdownButton: {
    backgroundColor: "#F4F5F6",
    borderRadius: 100,
    paddingHorizontal: 20,
    height: 58,
    justifyContent: "center",
    alignItems: "center",
  },
  countdownButtonText: {
    color: "#919191",
    fontSize: 17,
    fontWeight: "600",
    textAlign: "center",
    lineHeight: 22,
  },
  closeButton: {
    position: "absolute",
    top: 16,
    right: 16,
    width: 24,
    height: 24,
    justifyContent: "center",
    alignItems: "center",
    zIndex: 1,
  },
});

export { InitiateCallContent };
